import React from 'react';
import type { ImageSourcePropType, ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  IllusEmployeeFork,
  IllusEmpty_2,
  IllusFall,
  Image,
  SectionHeader,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useLocalizationContext } from '@order/Localization';

export const BagRailContainer: React.FC<BagRailContainerProps> = (props) => {
  const { hasError, isEmpty, children, ...railProps } = props;
  const isLoggedIn = useIsLoggedIn();

  if (!isLoggedIn)
    return <BagRailContainerSignedOut headerText={props.headerText} />;

  if (hasError) return <BagRailContainerError {...railProps} />;

  if (isEmpty)
    return (
      <BagRailContainerEmpty
        headerText={props.headerText}
        testID="bag.rewards-rail.empty"
      />
    );

  return <BagRailContainerWrapper>{children}</BagRailContainerWrapper>;
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const BagRailContainerSignedOut = (props: BagRailContainerInfoProps) => {
  const { t } = useLocalizationContext();

  return (
    <BagRailContainerInfoPanel
      headerText={props.headerText}
      description={t('bag.rewards.signed-out')}
      imageAssetUrl={IllusFall}
    />
  );
};

const BagRailContainerEmpty = (props: BagRailContainerInfoProps) => {
  const { t } = useLocalizationContext();

  return (
    <BagRailContainerInfoPanel
      headerText={props.headerText}
      description={t('bag.rewards.no-rewards')}
      imageAssetUrl={IllusEmployeeFork}
      testID={props.testID}
    />
  );
};

const BagRailContainerError = (props: BagRailContainerInfoProps) => {
  const { t } = useLocalizationContext();

  return (
    <BagRailContainerInfoPanel
      headerText={props.headerText}
      description={t('bag.rewards.error')}
      imageAssetUrl={IllusEmpty_2}
    />
  );
};

const BagRailContainerInfoPanel = (props: BagRailInfoPanelProps) => {
  const { headerText, description, imageAssetUrl, testID } = props;

  return (
    <BagRailContainerWrapper testID={testID}>
      <SectionHeader heading={headerText} />

      <View style={styles.infoPanelContent}>
        <BodyText size={4} style={styles.infoPanelDescription}>
          {description}
        </BodyText>

        <Image
          source={imageAssetUrl as ImageSourcePropType}
          resizeMode="contain"
          accessibilityIgnoresInvertColors
          style={styles.infoPanelIllustration}
        />
      </View>
    </BagRailContainerWrapper>
  );
};

const BagRailContainerWrapper = (props: ViewProps) => {
  const { match } = useResponsive();

  const wrapperStyle = [
    match([styles.wrapperXS, styles.wrapperSM]),
    props.style,
  ];

  return (
    <View style={wrapperStyle} testID={props.testID}>
      {props.children}
    </View>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapperXS: {
    paddingTop: theme.spacing.small,
    paddingBottom: 32,
  },
  wrapperSM: {
    paddingTop: theme.spacing.large,
    paddingBottom: theme.spacing.xlarge,
  },
  infoPanelContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing.medium,
    paddingBottom: 34,
  },
  infoPanelDescription: {
    paddingRight: theme.spacing.medium,
    maxWidth: 220,
  },
  infoPanelIllustration: {
    width: 120,
    height: 120,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type BagRailContainerProps = {
  readonly isEmpty?: boolean;
  readonly hasError?: boolean;
  readonly headerText: string;
};

type BagRailContainerInfoProps = {
  readonly headerText: string;
  // eslint-disable-next-line react/no-unused-prop-types
  readonly testID?: string;
};

type BagRailInfoPanelProps = {
  readonly description: string;
  readonly imageAssetUrl: string | ImageSourcePropType;
} & BagRailContainerInfoProps;
